import { ColDef, ICellRendererParams } from 'ag-grid-community';
import styles from './CallLimitHistory.module.scss';
import { FILE_STATUS } from '../../types';
import { Button } from '@navi/web-ui/lib/primitives';
import { DownloadIcon } from '@navi/web-ui/lib/icons';
import { fetchCallLimitOriginalFile, fetchCallLimitFailureReport } from '../../CallLimitActions';
import { useDispatch } from 'react-redux';
import { useMemo } from 'react';

export const useColumnDefs = (): {
  columnDefs: ColDef[];
  handleOriginalFileClick: (fileReferenceId: string) => void;
  handleFailureReportClick: (fileReferenceId: string, failureReportExists: boolean) => void;
} => {
  const dispatch = useDispatch();

  const handleOriginalFileClick = (fileReferenceId: string) => {
    dispatch(fetchCallLimitOriginalFile(fileReferenceId));
  };

  const handleFailureReportClick = (fileReferenceId: string) => {
    dispatch(fetchCallLimitFailureReport(fileReferenceId));
  };

  const columnDefs: ColDef[] = useMemo(
    () => [
      {
        field: 'totalLans',
        headerName: 'Total no. of LANs',
        wrapHeaderText: true,
        minWidth: 110,
        maxWidth: 140,
        headerComponentParams: { width: 140 },
        cellStyle: { cursor: 'auto' },
        cellRenderer: (params: ICellRendererParams) => {
          const { data } = params;
          if (data?.status === FILE_STATUS.FAILED) {
            return '-';
          }
          return <span>{data?.totalLans ?? '-'}</span>;
        }
      },
      {
        field: 'uploaded',
        headerName: 'Uploaded',
        minWidth: 60,
        maxWidth: 80,
        headerComponentParams: { width: 100 },
        cellStyle: { cursor: 'auto' },
        cellRenderer: (params: ICellRendererParams) => {
          const { data } = params;
          if (data?.status === FILE_STATUS.FAILED) {
            return '-';
          }
          return <span>{data?.uploaded ?? '-'}</span>;
        }
      },
      {
        field: 'failed',
        headerName: 'Failed',
        minWidth: 60,
        maxWidth: 80,
        headerComponentParams: { width: 80 },
        cellStyle: { cursor: 'auto' },
        cellRenderer: (params: ICellRendererParams) => {
          const { data } = params;
          if (data?.status === FILE_STATUS.FAILED) {
            return '-';
          }
          const textColor = data?.failureReportExists ? 'var(--red-base)' : 'var(--grayscale-2)';
          return <span style={{ color: textColor }}>{data?.failed ?? 0}</span>;
        }
      },
      {
        field: 'uploadDateTime',
        headerName: 'Upload Date & Time',
        minWidth: 150,
        maxWidth: 200,
        wrapHeaderText: false,
        headerComponentParams: { width: 150 },
        cellStyle: { cursor: 'auto' },
        cellRenderer: (params: ICellRendererParams) => {
          const { data } = params;
          const textColor = 'var(--grayscale-3)';
          return <span style={{ color: textColor }}>{data?.uploadDateTime}</span>;
        }
      },
      {
        field: 'file',
        headerName: 'Original File',
        minWidth: 100,
        maxWidth: 130,
        headerComponentParams: { width: 100 },
        cellRenderer: (params: ICellRendererParams) => {
          const { data } = params;
          if (data?.status === FILE_STATUS.COMPLETED) {
            return (
              <Button
                variant="text"
                className={styles.buttonWrapper}
                style={{ color: 'var(--blue-base)', cursor: 'pointer' }}
                onClick={() => handleOriginalFileClick(data.file)}
                startAdornment={<DownloadIcon color="var(--blue-base)" />}
              >
                Download
              </Button>
            );
          }
          return '-';
        }
      },
      {
        field: 'failureReport',
        headerName: 'Failure Report',
        wrapHeaderText: false,
        minWidth: 100,
        maxWidth: 130,
        headerComponentParams: { width: 80 },
        cellRenderer: (params: ICellRendererParams) => {
          const { data } = params;
          const { failureReportExists } = data;
          if (failureReportExists) {
            return (
              <Button
                variant="text"
                onClick={() => handleFailureReportClick(data.file)}
                startAdornment={<DownloadIcon color="var(--blue-base)" />}
              >
                Download
              </Button>
            );
          }
          return null;
        }
      }
    ],
    []
  );

  return { columnDefs, handleOriginalFileClick, handleFailureReportClick };
};

export default useColumnDefs;
